import "@aws-amplify/ui-react/styles.css";

import { applyMode, Mode } from "@amzn/awsui-global-styles";
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { BrowserRouter as Router, Navigate,Route, Routes } from "react-router-dom";

import Layout from "./components/layout/Layout";
import { CartProvider } from "./contexts/CartContext";
import { ChatProvider } from "./contexts/ChatContext";
import { ChatHistoryProvider } from "./contexts/ChatHistoryContext";
import { ClipPreviewProvider } from "./contexts/ClipPreviewContext";
import { GroupProvider } from "./contexts/GroupProvider";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { PanelContentProvider } from "./contexts/PanelContentContext";
import { UserProvider } from "./contexts/UserContext";
import AdminAgentConfigurationPage from "./pages/AdminAgentConfigurationPage";
import AdminCachedMessageDetailsPage from "./pages/AdminCachedMessageDetailsPage";
import AdminCachedMessagesPage from "./pages/AdminCachedMessagesPage";
import AdminChatHistoryPage from "./pages/AdminChatHistoryPage";
import AdminEvalDatasetPage from "./pages/AdminEvalDatasetPage";
import AdminEvaluationPage from "./pages/AdminEvaluationPage";
import AdminExportLocationsPage from "./pages/AdminExportLocationsPage";
import AdminMetadataConfigurationPage from "./pages/AdminMetadataConfigurationPage";
import AdminUserAnalyticsPage from "./pages/AdminUserAnalyticsPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import CartPage from "./pages/CartPage";
import ChatPage from "./pages/ChatPage";
import LoadingPage from "./pages/LoadingPage";
import LoginPage from "./pages/LoginPage";
import { UserObject } from "./types/user";
import { STAGE } from "./utils/constants";
import { checkAdmin } from "./utils/utils";

const App: React.FC = () => {
  const [user, setUser] = useState<UserObject | null>(null);
  const [isCheckingUser, setIsCheckingUser] = useState(true);
  // Token for WebSocket
  const [token, setToken] = useState<string>("");

  // Set the mode to dark.
  useEffect(() => {
    applyMode(Mode.Dark);
  }, []);

  // Listen for auth events
  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signInWithRedirect":
          checkUser();
          break;
        case "signInWithRedirect_failure":
          setIsCheckingUser(false);
          break;
        case "customOAuthState":
        default:
          break;
      }
    });

    return unsubscribe;
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut({ global: true });
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, []);

  // Check if user is already authenticated
  const checkUser = useCallback(async () => {
    setIsCheckingUser(true);
    try {
      const currentUser = await getCurrentUser();
      const { tokens } = await fetchAuthSession();
      const attributes = await fetchUserAttributes();
      const groups = tokens?.accessToken?.payload["cognito:groups"] as string[];

      setUser({
        ...currentUser,
        attributes,
        isAdmin: checkAdmin(groups ?? []),
      });
      // Store the initial token so we know auth succeeded (gate rendering).
      setToken(tokens?.idToken?.toString() ?? "");
    } catch (error) {
      // Handle error
      console.error("Error checking user:", error);
      await handleSignOut();
    } finally {
      setIsCheckingUser(false);
    }
  }, [handleSignOut]);

  // Provides a fresh Cognito ID token on every call. Used by ChatProvider
  // so that WebSocket (re)connections always use a valid, non-expired token.
  // fetchAuthSession() transparently refreshes tokens when they're expired.
  const getToken = useCallback(async (): Promise<string> => {
    const { tokens } = await fetchAuthSession();
    return tokens?.idToken?.toString() ?? "";
  }, []);

  // Check if user is already authenticated
  useEffect(() => {
    checkUser();
  }, [checkUser]);

  // Show loading page.
  if (isCheckingUser) {
    return <LoadingPage />;
  }

  // Show login if there's no user or token
  if (!user || !token) {
    return <LoginPage />;
  }

  return (
    <>
      <Helmet>
        <title>
          PVLS: MAMBA{STAGE === "prod" ? "" : `  (${STAGE?.toUpperCase()})`}
        </title>
      </Helmet>
      <Router>
        <GroupProvider
          providers={[
            NotificationsProvider,
            PanelContentProvider,
            ClipPreviewProvider,
            {
              provider: UserProvider,
              props: {
                user,
              },
            },
            ChatHistoryProvider,
            {
              provider: ChatProvider,
              props: {
                getToken,
                user,
              },
            },
            {
              provider: CartProvider,
              props: {
                email: user.attributes.email,
              },
            },
          ]}
        >
          <Layout signOut={handleSignOut}>
            <Routes>
              <Route path="/" element={<ChatPage />} />
              <Route path="/cart" element={<CartPage />} />
              {user.isAdmin && (
                <>
                  <Route
                    path="/admin/cached-messages"
                    element={<AdminCachedMessagesPage />}
                  />
                  <Route
                    path="/admin/cached-messages/:messageId"
                    element={<AdminCachedMessageDetailsPage />}
                  />
                  <Route
                    path="/admin/chat-history"
                    element={<AdminChatHistoryPage />}
                  />
                  <Route
                    path="/admin/metadata-configuration"
                    element={<AdminMetadataConfigurationPage />}
                  />
                  <Route
                    path="/admin/agent-configuration"
                    element={<AdminAgentConfigurationPage />}
                  />
                  <Route
                    path="/admin/evaluation"
                    element={<AdminEvaluationPage />}
                  />
                  <Route
                    path="/admin/eval-dataset"
                    element={<AdminEvalDatasetPage />}
                  />
                  <Route
                    path="/admin/export-locations"
                    element={<AdminExportLocationsPage />}
                  />
                  <Route
                    path="/admin/user-analytics"
                    element={<AdminUserAnalyticsPage />}
                  />
                  <Route
                    path="/admin/users"
                    element={<AdminUsersPage />}
                  />
                </>
              )}
              <Route path="*" element={<Navigate to="/"/>}/>
            </Routes>
          </Layout>
        </GroupProvider>
      </Router>
    </>
  );
};

export default App;
